import { useState, useRef, useCallback } from 'react';

const MAX_HISTORY = 50;

/**
 * Undo/redo hook with support for drag preview (non-committed) changes.
 *
 * setValue(v)      – push to history (used by panel inputs)
 * previewValue(v)  – update display without pushing (used during drag)
 * commitPreview()  – push the current preview to history (used on mouseup)
 * undo() / redo()  – step through history
 * reset(v)         – replace history with a single entry
 */
const useUndoRedo = (initialValue) => {
  const [history, setHistory] = useState([initialValue]);
  const [pointer, setPointer] = useState(0);
  // preview holds a transient value (during drag) that hasn't been committed
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);

  const current = preview !== null ? preview : history[pointer];

  const setValue = useCallback((val) => {
    setPreview(null);
    previewRef.current = null;
    setHistory(prev => {
      const next = prev.slice(0, pointer + 1);
      next.push(typeof val === 'function' ? val(prev[pointer]) : val);
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setPointer(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [pointer]);

  const previewValue = useCallback((val) => {
    const v = typeof val === 'function' ? val(previewRef.current ?? history[pointer]) : val;
    setPreview(v);
    previewRef.current = v;
  }, [history, pointer]);

  const commitPreview = useCallback(() => {
    if (previewRef.current === null) return;
    const val = previewRef.current;
    setPreview(null);
    previewRef.current = null;
    setHistory(prev => {
      const next = prev.slice(0, pointer + 1);
      next.push(val);
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setPointer(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [pointer]);

  const undo = useCallback(() => {
    setPreview(null);
    previewRef.current = null;
    setPointer(prev => Math.max(0, prev - 1));
  }, []);

  const redo = useCallback(() => {
    setPreview(null);
    previewRef.current = null;
    setPointer(prev => Math.min(history.length - 1, prev + 1));
  }, [history.length]);

  const reset = useCallback((val) => {
    setPreview(null);
    previewRef.current = null;
    setHistory([val]);
    setPointer(0);
  }, []);

  return {
    value: current,
    setValue,
    previewValue,
    commitPreview,
    undo,
    redo,
    canUndo: pointer > 0,
    canRedo: pointer < history.length - 1,
    reset
  };
};

export default useUndoRedo;
